import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import {
  ptoKfoApi,
  useGetKfoFacetsQuery,
  useGetKfoLinesQuery,
  type KfoLine,
  type KfoLinesArgs,
  type KfoReport,
  type KfoReportKind,
} from '@shared/api';
import { useAppDispatch } from '@app/hooks';
import { useDebouncedValue } from '@shared/lib/useDebouncedValue';
import { Button } from '@shared/ui/Button';
import { CreateReportForm } from './CreateReportForm';
import { useQtySaver } from './useQtySaver';
import {
  Actions,
  Banner,
  DataTable,
  Dialog,
  Field,
  Filters,
  Muted,
  Pager,
  QtyInput,
  Row,
  SmallInput,
  SmallSelect,
  TableWrap,
  Tag,
  errorText,
  isNotFound,
  fmtDate,
  fmtNum,
  isRfiKind,
  parseQty,
  reportTitle,
} from './ui';

/** Фильтры реестра со значениями из `/kfo/facets`. */
type FacetFilters = {
  contract_no: string;
  zone: string;
  titul: string;
  mark: string;
  work_kind: string;
  responsible: string;
};

const EMPTY_FILTERS: FacetFilters = {
  contract_no: '',
  zone: '',
  titul: '',
  mark: '',
  work_kind: '',
  responsible: '',
};

/** Сколько подсказок листов РД держать в datalist (их бывают тысячи). */
const RD_SUGGEST_LIMIT = 200;

type RowCheck = { level: 'error' | 'warn'; message: string } | null;

/**
 * Проверка объёма в строке прямо при вводе — те же правила, что у сервера
 * (§6): для приёмочного RFI Факт СМР накопительно > 0 и ≥ предъявляемого
 * (ошибка), для всех — накопительно больше ФО по РД и «нет в срезе»
 * (предупреждения). Окончательное слово — за «Проверить» на сервере.
 */
export function checkLineQty(kind: KfoReportKind, line: KfoLine, qty: number): RowCheck {
  if (!qty || qty <= 0) return null;
  if (kind === 'rfi_acceptance') {
    const fc = line.fact_cum ?? 0;
    if (fc <= 0) {
      return {
        level: 'error',
        message: 'Факт СМР накопительно = 0 — в приёмочный RFI предъявлять нельзя.',
      };
    }
    if (fc < qty) {
      return {
        level: 'error',
        message: `Больше, чем Факт СМР накопительно (${fmtNum(fc)}).`,
      };
    }
  }
  const prior =
    kind === 'rfi_acceptance'
      ? line.accepted_sk
      : kind === 'rfi_geodetic'
        ? line.accepted_gk
        : line.fact_cum;
  if (line.fo_total !== null && (prior ?? 0) + qty > line.fo_total + 1e-9) {
    return {
      level: 'warn',
      message: `Накопительно ${fmtNum((prior ?? 0) + qty)} больше ФО по РД (${fmtNum(line.fo_total)}).`,
    };
  }
  if (line.absent) {
    return { level: 'warn', message: 'Строки нет в последнем срезе Заказчика.' };
  }
  return null;
}

export function KfoRegistryTab({
  projectId,
  canWrite,
  reports,
  currentReportId,
  onCurrentReport,
  selection,
  onSelectionChange,
  onOpenLine,
  onGuaranteeLetter,
}: {
  projectId: string;
  canWrite: boolean;
  reports: KfoReport[];
  currentReportId: string | null;
  onCurrentReport: (id: string | null) => void;
  selection: Map<string, KfoLine>;
  onSelectionChange: (next: Map<string, KfoLine>) => void;
  onOpenLine: (lineId: string) => void;
  onGuaranteeLetter: () => void;
}) {
  const dispatch = useAppDispatch();
  const { data: facets, isSuccess: facetsOk } = useGetKfoFacetsQuery(projectId, {
    skip: !projectId,
  });

  const [q, setQ] = useState('');
  const [rd, setRd] = useState('');
  const [ep, setEp] = useState('');
  const [filters, setFilters] = useState<FacetFilters>(EMPTY_FILTERS);
  const [absent, setAbsent] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [createOpen, setCreateOpen] = useState(false);
  const [staleDraft, setStaleDraft] = useState(false);

  const dq = useDebouncedValue(q.trim(), 400);
  const drd = useDebouncedValue(rd.trim(), 400);
  const dep = useDebouncedValue(ep.trim(), 400);

  const drafts = useMemo(() => reports.filter((r) => r.status === 'draft'), [reports]);
  const current = reports.find((r) => r.id === currentReportId) ?? null;
  const editable = canWrite && current?.status === 'draft';
  const saver = useQtySaver(editable ? current.id : null);

  const args: KfoLinesArgs = {
    projectId,
    q: dq || undefined,
    ...filters,
    rd_sheet_code: drd || undefined,
    ep_code: dep || undefined,
    absent: absent || undefined,
    // id черновика известен сразу (запомнен), не ждём списка отчётов — без лишнего запроса.
    report_id: currentReportId ?? undefined,
    page: page + 1,
    per_page: perPage,
  };
  const { data, isFetching, error } = useGetKfoLinesQuery(args, { skip: !projectId });
  const lines = data?.items ?? [];
  const total = data?.total ?? 0;

  // Запомненный черновик удалён в другом месте (закэшированный список отчётов
  // о нём ещё не знает): сервер отвечает 404 на `report_id`. Раздел при этом
  // развёрнут — фасеты пришли, — значит, это не «нет бэкенда»: забываем
  // черновик, перечитываем список и говорим об этом, а не «загрузите срез».
  const draftGone = !!currentReportId && isNotFound(error) && facetsOk;
  useEffect(() => {
    if (!draftGone) return;
    setStaleDraft(true);
    onCurrentReport(null);
    dispatch(ptoKfoApi.util.invalidateTags([{ type: 'KfoReports', id: 'LIST' }]));
  }, [draftGone, onCurrentReport, dispatch]);
  useEffect(() => {
    if (currentReportId) setStaleDraft(false);
  }, [currentReportId]);

  const rdOptions = useMemo(() => {
    const all = facets?.rd_sheet_codes ?? [];
    const needle = rd.trim().toLowerCase();
    const hit = needle ? all.filter((c) => c.toLowerCase().includes(needle)) : all;
    return hit.slice(0, RD_SUGGEST_LIMIT);
  }, [facets, rd]);

  function setFilter<K extends keyof FacetFilters>(k: K, v: string) {
    setFilters((prev) => ({ ...prev, [k]: v }));
    setPage(0);
  }

  function resetFilters() {
    setFilters(EMPTY_FILTERS);
    setQ('');
    setRd('');
    setEp('');
    setAbsent(false);
    setPage(0);
  }

  const hasFilters = q || rd || ep || absent || Object.values(filters).some((v) => v !== '');

  // ── отметки строк (для гарантийного письма) ──
  const pageAllChecked = lines.length > 0 && lines.every((l) => selection.has(l.id));
  function toggleLine(l: KfoLine) {
    const next = new Map(selection);
    if (next.has(l.id)) next.delete(l.id);
    else next.set(l.id, l);
    onSelectionChange(next);
  }
  function togglePage() {
    const next = new Map(selection);
    if (pageAllChecked) lines.forEach((l) => next.delete(l.id));
    else lines.forEach((l) => next.set(l.id, l));
    onSelectionChange(next);
  }

  /** Enter в поле объёма — сохранить и перейти к следующей строке. */
  function onQtyKey(e: KeyboardEvent<HTMLInputElement>, idx: number) {
    if (e.key !== 'Enter' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    saver.flush();
    const to = e.key === 'ArrowUp' ? idx - 1 : idx + 1;
    const el = document.querySelector<HTMLInputElement>(`input[data-kfo-qty="${to}"]`);
    el?.focus();
    el?.select();
  }

  const qtyHeader = current
    ? isRfiKind(current.kind)
      ? 'Предъявлено в RFI'
      : 'Физический объём'
    : null;

  const facetSelect = (
    key: keyof FacetFilters,
    label: string,
    values: string[] | undefined,
    all: string,
  ) =>
    values && values.length > 0 ? (
      <Field>
        {label}
        <SmallSelect value={filters[key]} onChange={(e) => setFilter(key, e.target.value)}>
          <option value="">{all}</option>
          {values.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </SmallSelect>
      </Field>
    ) : null;

  return (
    <div>
      {/* Текущий отчёт для ввода объёма в строке. */}
      <Row>
        <Actions>
          <Muted>Вводить объём в:</Muted>
          <SmallSelect
            value={current?.id ?? ''}
            onChange={(e) => onCurrentReport(e.target.value || null)}
            style={{ maxWidth: 360 }}
          >
            <option value="">— только просмотр —</option>
            {current && current.status !== 'draft' && (
              <option value={current.id}>{reportTitle(current)} (выпущен)</option>
            )}
            {drafts.map((r) => (
              <option key={r.id} value={r.id}>
                {reportTitle(r)}
              </option>
            ))}
          </SmallSelect>
          {canWrite && (
            <Button
              $size="sm"
              $variant="secondary"
              type="button"
              onClick={() => setCreateOpen(true)}
            >
              + Новый черновик
            </Button>
          )}
          {current && (
            <Muted>
              {saver.status === 'saving'
                ? 'Сохраняю…'
                : saver.status === 'saved'
                  ? 'Сохранено'
                  : saver.status === 'error'
                    ? ''
                    : current.status === 'draft'
                      ? 'Пустое поле или 0 — строка убирается из отчёта'
                      : 'Отчёт выпущен — объём только для просмотра'}
            </Muted>
          )}
        </Actions>
      </Row>
      {saver.error && <Banner $tone="danger">{saver.error}</Banner>}
      {staleDraft && (
        <Banner>
          Черновик, в который вводился объём, удалён или недоступен. Выберите другой черновик или
          создайте новый.
        </Banner>
      )}

      <Filters>
        <Field style={{ flex: '1 1 220px' }}>
          Поиск
          <SmallInput
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Ключ, наименование ЕР, элемент…"
          />
        </Field>
        {facetSelect('contract_no', 'Договор', facets?.contracts, 'все договоры')}
        {facetSelect('zone', 'Зона', facets?.zones, 'все зоны')}
        {facetSelect('titul', 'Титул', facets?.tituls, 'все титулы')}
        {facetSelect('mark', 'Марка', facets?.marks, 'все марки')}
        {facetSelect('work_kind', 'Вид работ', facets?.work_kinds, 'все виды')}
        {facetSelect('responsible', 'Ответственный Заказчика', facets?.responsibles, 'все')}
        <Field>
          Лист РД
          <SmallInput
            value={rd}
            list="kfo-rd-sheets"
            onChange={(e) => {
              setRd(e.target.value);
              setPage(0);
            }}
            placeholder="часть шифра листа"
            title="Поиск по вхождению: достаточно части шифра"
            style={{ width: 200 }}
          />
          <datalist id="kfo-rd-sheets">
            {rdOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </Field>
        <Field>
          Шифр ЕР
          <SmallInput
            value={ep}
            onChange={(e) => {
              setEp(e.target.value);
              setPage(0);
            }}
            placeholder="часть шифра ЕР"
            title="Поиск по вхождению: достаточно части шифра"
            style={{ width: 140 }}
          />
        </Field>
        <Field style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 34 }}>
          <input
            type="checkbox"
            checked={absent}
            onChange={(e) => {
              setAbsent(e.target.checked);
              setPage(0);
            }}
          />
          только «нет в срезе»
        </Field>
        {hasFilters && (
          <Button $size="sm" $variant="ghost" type="button" onClick={resetFilters}>
            Сбросить
          </Button>
        )}
      </Filters>

      {error && !isNotFound(error) ? (
        <Banner $tone="danger">{errorText(error, 'Не удалось загрузить строки КФО.')}</Banner>
      ) : null}

      <TableWrap style={{ opacity: isFetching ? 0.6 : 1 }}>
        <DataTable>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={pageAllChecked}
                  onChange={togglePage}
                  title="Отметить все строки страницы"
                  aria-label="Отметить все строки страницы"
                />
              </th>
              <th>Ключ</th>
              <th>Лист РД / рев.</th>
              <th>Шифр и наименование ЕР</th>
              <th>Элемент</th>
              <th>Ед.</th>
              <th className="num">ФО по РД</th>
              <th className="num">Факт накоп.</th>
              <th className="num">Принято СК</th>
              <th className="num">Принято ГК</th>
              <th className="num">ИД принято</th>
              <th className="num">ПА</th>
              <th className="num">НЗП</th>
              {qtyHeader && <th className="num stick-r">{qtyHeader}</th>}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td colSpan={qtyHeader ? 14 : 13} style={{ textAlign: 'center', padding: 32 }}>
                  <Muted>
                    {isFetching
                      ? 'Загрузка…'
                      : hasFilters
                        ? 'Под фильтры ничего не попало.'
                        : 'Строк КФО нет. Загрузите срез Заказчика во вкладке «Импорт».'}
                  </Muted>
                </td>
              </tr>
            )}
            {lines.map((l, idx) => {
              const text = saver.value(l.id, l.draft_qty);
              const invalid = saver.isInvalid(l.id);
              const parsed = parseQty(text) ?? 0;
              const check = current ? checkLineQty(current.kind, l, parsed) : null;
              return (
                <tr
                  key={l.id}
                  data-click="true"
                  data-tone={l.absent ? 'muted' : undefined}
                  onClick={() => onOpenLine(l.id)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.has(l.id)}
                      onChange={() => toggleLine(l)}
                      aria-label={`Отметить строку ${l.ext_key}`}
                    />
                  </td>
                  <td className="nowrap" title={`Договор ${l.contract_no}`}>
                    {l.ext_key}
                    {l.absent && (
                      <div>
                        <Tag $tone="warning" title="Строки нет в последнем срезе Заказчика">
                          нет в срезе
                        </Tag>
                      </div>
                    )}
                  </td>
                  <td className="nowrap">
                    {l.rd_sheet_code}
                    {l.rd_revision ? (
                      <Muted style={{ fontSize: 11 }}>рев. {l.rd_revision}</Muted>
                    ) : null}
                  </td>
                  <td className="wrap" style={{ minWidth: 200, maxWidth: 300 }}>
                    <b>{l.ep_code}</b>
                    {l.ep_name ? <Muted style={{ fontSize: 11 }}>{l.ep_name}</Muted> : null}
                  </td>
                  <td className="wrap" style={{ minWidth: 120, maxWidth: 220 }}>
                    {l.element}
                  </td>
                  <td className="nowrap">{l.unit}</td>
                  <td className="num">{fmtNum(l.fo_total)}</td>
                  <td
                    className="num"
                    title={
                      l.last_fact_date ? `Дата учтённого факта: ${fmtDate(l.last_fact_date)}` : ''
                    }
                  >
                    {fmtNum(l.fact_cum)}
                  </td>
                  <td className="num">{fmtNum(l.accepted_sk)}</td>
                  <td className="num">{fmtNum(l.accepted_gk)}</td>
                  <td
                    className="num"
                    title={`Предъявлено ИД: ${fmtNum(l.id_presented) || '—'}; на подписании: ${fmtNum(l.id_on_signing) || '—'}`}
                  >
                    {fmtNum(l.id_accepted)}
                  </td>
                  <td className="num">{fmtNum(l.pa_accepted)}</td>
                  <td className="num">{fmtNum(l.nzp)}</td>
                  {qtyHeader && (
                    <td className="num stick-r" onClick={(e) => e.stopPropagation()}>
                      {editable ? (
                        <QtyInput
                          data-kfo-qty={idx}
                          value={text}
                          inputMode="decimal"
                          $invalid={invalid || saver.isFailed(l.id) || check?.level === 'error'}
                          $warn={check?.level === 'warn'}
                          $pending={saver.isPending(l.id)}
                          title={
                            invalid
                              ? 'Введите число (запятая — десятичный разделитель)'
                              : (check?.message ?? '')
                          }
                          onChange={(e) => saver.set(l.id, e.target.value)}
                          onBlur={() => saver.flush()}
                          onKeyDown={(e) => onQtyKey(e, idx)}
                          aria-label={`Объём по строке ${l.ext_key}`}
                        />
                      ) : (
                        fmtNum(l.draft_qty)
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </DataTable>
      </TableWrap>

      <Pager
        page={page}
        perPage={perPage}
        total={total}
        onPage={setPage}
        onPerPage={(n) => {
          setPerPage(n);
          setPage(0);
        }}
      />

      {selection.size > 0 && (
        <Banner $tone="info">
          <Actions>
            <span>
              Отмечено строк: <b>{selection.size}</b> (отметки сохраняются при смене страницы и
              фильтров).
            </span>
            {canWrite && (
              <Button $size="sm" type="button" onClick={onGuaranteeLetter}>
                Гарантийное письмо…
              </Button>
            )}
            <Button
              $size="sm"
              $variant="ghost"
              type="button"
              onClick={() => onSelectionChange(new Map())}
            >
              Снять отметки
            </Button>
          </Actions>
        </Banner>
      )}

      <Dialog
        open={createOpen}
        title="Новый черновик"
        onClose={() => setCreateOpen(false)}
        width={480}
      >
        <CreateReportForm
          projectId={projectId}
          onCreated={(r) => {
            setCreateOpen(false);
            onCurrentReport(r.id);
          }}
        />
      </Dialog>
    </div>
  );
}
